import { lazy, Suspense } from 'react';
import { Navigation } from '@/components/Navigation';

const Footer = lazy(() => import('@/components/Footer'));

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[hsl(var(--light-bg-0))]">
      <Navigation />
      
      <main data-nav-theme="light" className="pt-24 sm:pt-28 lg:pt-32">
        <div className="max-w-4xl mx-auto px-6 py-16 sm:py-20 lg:py-32">
          {/* Header */}
          <div className="mb-12 lg:mb-16">
            <h1 className="text-[clamp(2rem,4.5vw,3.5rem)] lg:text-[clamp(2.5rem,5vw,4rem)] font-bold tracking-[-0.05em] text-[hsl(var(--light-text))] mb-4">
              Privacy Policy
            </h1>
            <div className="space-y-1">
              <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))]">
                Damiankeyes.com
              </p>
              <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))]">
                DK Music Business Academy Ltd
              </p>
              <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))]">
                Last updated: 21 November 2025
              </p>
            </div>
          </div>

          {/* Introduction */}
          <div className="space-y-6 mb-12">
            <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              This Privacy Policy explains how Damiankeyes.com collects, uses, and protects information when you visit this website. This site is intended as a link hub directing visitors to other platforms owned and operated by Damian Keyes and DK Music Business Academy Ltd.
            </p>
          </div>

          {/* Section 1 */}
          <div className="space-y-6 mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-[hsl(var(--light-text))]">
              1. Who We Are
            </h2>
            <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              Damiankeyes.com is operated by:
            </p>
            <div className="pl-6 space-y-2">
              <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
                DK Music Business Academy Ltd<br />
                Registered office: Palmeira Avenue Mansions, 19 Church Road, Hove, East Sussex, BN3 2FA<br />
                Email: hello@dk-mba.com
              </p>
            </div>
            <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              We are the data controller for this website under UK GDPR.
            </p>
          </div>

          {/* Section 2 */}
          <div className="space-y-6 mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-[hsl(var(--light-text))]">
              2. What Information We Collect
            </h2>
            <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              Damiankeyes.com does not collect personal information directly from users. We do not provide forms, accounts, signups, or payment services on this site.
            </p>
            <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              However, some non-personal information may be collected automatically for basic performance and analytics, including:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              <li>IP address</li>
              <li>Device type</li>
              <li>Browser type</li>
              <li>Pages viewed</li>
              <li>Clicks on external links</li>
              <li>General usage data</li>
              <li>Cookies (see Section 5)</li>
            </ul>
            <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              This information cannot identify you personally and is used only to understand website performance.
            </p>
          </div>

          {/* Section 3 */}
          <div className="space-y-6 mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-[hsl(var(--light-text))]">
              3. How We Use This Information
            </h2>
            <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              We use basic analytics data to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              <li>Monitor website performance</li>
              <li>Understand how visitors use the site</li>
              <li>Improve layout, navigation, and functionality</li>
              <li>Ensure the site loads correctly on different devices</li>
            </ul>
            <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              We do not use the data to profile users or run targeted advertising.
            </p>
          </div>

          {/* Section 4 */}
          <div className="space-y-6 mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-[hsl(var(--light-text))]">
              4. Links to External Websites
            </h2>
            <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              Damiankeyes.com contains links to platforms such as:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              <li>YouTube</li>
              <li>Instagram</li>
              <li>TikTok</li>
              <li>Facebook</li>
              <li>DK-MBA.com</li>
              <li>DK-Artists.com</li>
              <li>Other Damian Keyes brand sites and services</li>
            </ul>
            <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              Once you click an external link, you are governed by the privacy policy of that platform or website. We are not responsible for the privacy practices or content of external sites.
            </p>
            <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              For purchases, newsletter signups, services, memberships, or consultations, refer to the Privacy Policies on those respective sites.
            </p>
          </div>

          {/* Section 5 */}
          <div className="space-y-6 mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-[hsl(var(--light-text))]">
              5. Cookies and Tracking Technologies
            </h2>
            <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              This site may use cookies and basic tracking tools to analyse website performance. These may include:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              <li>Google Analytics</li>
              <li>Meta Pixel</li>
              <li>Browser cookies</li>
            </ul>
            <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              Cookies help us understand how users move through the site and which pages or links are clicked.
            </p>
            <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              You can disable cookies in your browser settings at any time.
            </p>
          </div>

          {/* Section 6 */}
          <div className="space-y-6 mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-[hsl(var(--light-text))]">
              6. Sharing of Information
            </h2>
            <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              We do not sell or share personal data.
            </p>
            <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              Non-personal analytics data may be processed by third-party tools such as Google Analytics, subject to their privacy practices and safeguards.
            </p>
          </div>

          {/* Section 7 */}
          <div className="space-y-6 mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-[hsl(var(--light-text))]">
              7. Data Security
            </h2>
            <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              Although we do not store personal data, we take steps to ensure the website is secure and protected from unauthorised access or misuse.
            </p>
          </div>

          {/* Section 8 */}
          <div className="space-y-6 mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-[hsl(var(--light-text))]">
              8. Children's Privacy
            </h2>
            <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              This site is not intended for individuals under 18. We do not knowingly collect any data from minors.
            </p>
          </div>

          {/* Section 9 */}
          <div className="space-y-6 mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-[hsl(var(--light-text))]">
              9. Your Rights
            </h2>
            <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              Because Damiankeyes.com does not collect personal information, most data rights under UK GDPR do not apply here.
            </p>
            <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              However, if you believe technical data relating to your visit could be linked to you personally, you may contact us at hello@dk-mba.com.
            </p>
          </div>

          {/* Section 10 */}
          <div className="space-y-6 mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-[hsl(var(--light-text))]">
              10. Changes to This Policy
            </h2>
            <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              We may update this Privacy Policy if required. Any changes will appear on this page with an updated date.
            </p>
          </div>

          {/* Section 11 */}
          <div className="space-y-6 mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-[hsl(var(--light-text))]">
              11. Contact Us
            </h2>
            <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
              If you have questions about this Privacy Policy, contact:
            </p>
            <div className="pl-6 space-y-2">
              <p className="text-[0.9375rem] sm:text-[1.0625rem] text-[hsl(var(--light-text-muted))] leading-relaxed">
                DK Music Business Academy Ltd<br />
                Email: hello@dk-mba.com<br />
                Registered office: Palmeira Avenue Mansions, 19 Church Road, Hove, East Sussex, BN3 2FA
              </p>
            </div>
          </div>
        </div>
      </main>

      <Suspense fallback={<div />}>
        <Footer />
      </Suspense>
    </div>
  );
}
